import React, { useState } from 'react';
import classNames from 'classnames';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import { PrimaryButton, SecondaryButton } from '../../components';
import css from './DeleteAccountPanel.module.css';

/**
 * DeleteAccountPanel — lets authenticated users request and confirm
 * deletion of their account (GDPR Article 17 — Right to Erasure).
 *
 * Two-step flow:
 *   1. Click "Delete account" → POST /api/gdpr/request-deletion
 *   2. Confirm with token (shown in UI; in prod → email) → POST /api/gdpr/confirm-deletion
 *
 * @param {string} [props.className]
 */
const DeleteAccountPanel = ({ className }) => {
  const intl = useIntl();
  const [step, setStep] = useState('initial'); // initial | requested | confirming | done | error
  const [token, setToken] = useState('');
  const [confirmToken, setConfirmToken] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleRequest = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/gdpr/request-deletion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      setToken(data.token || '');
      setStep('requested');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/gdpr/confirm-deletion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: confirmToken || token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Confirmation failed');
      setStep('done');
      // Force logout and redirect after deletion
      setTimeout(() => { window.location.href = '/'; }, 2000);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      await fetch('/api/gdpr/cancel-deletion', { method: 'POST' });
    } catch (_) {}
    setStep('initial');
    setToken('');
    setConfirmToken('');
    setError(null);
    setLoading(false);
  };

  return (
    <div className={classNames(css.root, className)}>
      <h3 className={css.title}>
        <FormattedMessage id="DeleteAccountPanel.title" />
      </h3>
      <p className={css.description}>
        <FormattedMessage id="DeleteAccountPanel.description" />
      </p>

      {error && <p className={css.error}>{error}</p>}

      {step === 'initial' && (
        <SecondaryButton
          className={css.deleteBtn}
          onClick={handleRequest}
          inProgress={loading}
        >
          <FormattedMessage id="DeleteAccountPanel.requestDeletion" />
        </SecondaryButton>
      )}

      {step === 'requested' && (
        <div className={css.confirmStep}>
          <p className={css.confirmText}>
            <FormattedMessage id="DeleteAccountPanel.tokenSent" />
          </p>
          {token && (
            <p className={css.tokenDisplay}>
              <FormattedMessage id="DeleteAccountPanel.yourToken" />: <code>{token}</code>
            </p>
          )}
          <input
            type="text"
            className={css.tokenInput}
            placeholder={intl.formatMessage({ id: 'DeleteAccountPanel.tokenPlaceholder' })}
            value={confirmToken}
            onChange={e => setConfirmToken(e.target.value)}
          />
          <div className={css.buttons}>
            <PrimaryButton
              className={css.confirmBtn}
              onClick={handleConfirm}
              inProgress={loading}
              disabled={!confirmToken && !token}
            >
              <FormattedMessage id="DeleteAccountPanel.confirmDeletion" />
            </PrimaryButton>
            <SecondaryButton onClick={handleCancel} inProgress={loading}>
              <FormattedMessage id="DeleteAccountPanel.cancel" />
            </SecondaryButton>
          </div>
        </div>
      )}

      {step === 'done' && (
        <p className={css.success}>
          <FormattedMessage id="DeleteAccountPanel.deleted" />
        </p>
      )}
    </div>
  );
};

export default DeleteAccountPanel;
